import { Component, ElementRef, HostListener, OnDestroy, OnInit, ViewChild } from '@angular/core';
import { HttpClient } from '@angular/common/http';
import { ActivatedRoute, Router } from '@angular/router';
import { Subject, Subscription, forkJoin, from, of } from 'rxjs';
import { catchError, concatMap, debounceTime, distinctUntilChanged, map, switchMap, toArray } from 'rxjs/operators';
import {
  SaveSupportReminderRequest,
  SupportColumn,
  SupportLabel,
  SupportMailService,
  SupportMailThreadSummary,
  SupportPerson,
  SupportReminder,
  SupportService,
  SupportTicket,
  SupportTicketMailThreadRef,
  SupportTicketOrderRef,
  SupportTicketPriority,
  SupportTicketStatus,
  UpdateSupportTicketRequest,
} from '../api';
import { UserDirectoryEntry, UserDirectoryService } from '../../users/user-directory.service';
import { AuthService } from '../../auth/auth.service';
import { ConfirmDialogService } from '../../confirm-dialog/confirm-dialog.service';
import { NotificationService } from '../../notification/notification.service';
import { MENU_MAIL, userHasMenu } from '../../auth/menu-access';

// ============================================================================
// Support — vevői problémák kanban táblája + task szerkesztő modál.
// Design: erp-design/screens/10-support.html, logika: 23-logic.js supportVals()
// (supTasks/supLabels/supModal/supDraft), SLA: supSLA/supDeadline().
// Adat: SupportService (support/tickets, support/labels — CONTRACTS.md 2.),
// rendelés-kereséshez a meglévő GET orders?search= végpont.
// ============================================================================

interface SupAvatarVM {
  initial: string;
  color: string;
  title: string;
}

interface SupDeadlineVM {
  overdue: boolean;
  soon: boolean;
  /** pl. „5 óra van hátra” / „Lejárt 2 napja” */
  label: string;
  /** pl. „07.08. 07:30” */
  dueStr: string;
  color: string;
  bg: string;
}

interface SupCardVM {
  ticket: SupportTicket;
  title: string;
  excerpt: string;
  shopName: string | null;
  shopColor: string;
  labels: SupportLabel[];
  orderLabel: string | null;
  deadline: SupDeadlineVM | null;
  prioLabel: string;
  prioClass: string;
  dateLabel: string;
  commentCount: number;
  avatars: SupAvatarVM[];
}

/** Emlékeztető sor a Support oldal tetején. */
interface SupReminderVM {
  reminder: SupportReminder;
  deadline: SupDeadlineVM | null;
  avatars: SupAvatarVM[];
  done: boolean;
  /** „szept. 16. 14:00" vagy null */
  dueStr: string | null;
}

/** Az emlékeztető űrlap (új és szerkesztés is). */
interface SupReminderForm {
  id: number | null;
  title: string;
  description: string;
  /** datetime-local érték ('YYYY-MM-DDTHH:mm') vagy '' */
  due: string;
  assigneeIds: number[];
}

interface SupColumnVM {
  /** support_column.id (alapértelmezett, még nem mentett oszlopnál negatív) */
  id: number;
  key: SupportTicketStatus;
  color: string;
  isDone: boolean;
  cards: SupCardVM[];
}

interface SupCommentVM {
  /** mentett komment id-ja (új, még nem mentett taskon null) */
  id: number | null;
  who: string;
  text: string;
  time: string;
  avatar: SupAvatarVM;
  /** csak a szerző törölheti a saját hozzászólását */
  canDelete: boolean;
}

interface SupOrderResultVM {
  ref: SupportTicketOrderRef;
  name: string;
  meta: string;
}

interface SupDraft {
  title: string;
  description: string;
  status: SupportTicketStatus;
  priority: SupportTicketPriority;
  shop: string | null;
  order: SupportTicketOrderRef | null;
  labelIds: number[];
  /** felelősök (user id) — ők kapják a határidő előtti e-mailt */
  assigneeIds: number[];
  /** kapcsolt e-mail beszélgetések (Levelezés) — mentéskor kerülnek át */
  mailThreads: SupportTicketMailThreadRef[];
  /** szerveren tárolt csatolmány-fájlnevek */
  attachments: string[];
  /** még nem mentett (új task) képek: fájl + helyi előnézet */
  pendingImages: { file: File; previewUrl: string }[];
}

interface SupModalState {
  /** null = még nem mentett, új task */
  ticketId: number | null;
  isNew: boolean;
  draft: SupDraft;
  creator: string;
  createdAt: string | null;
  metaLine: string;
  creatorAvatar: SupAvatarVM;
  comment: string;
  /** új (még nem mentett) taskhoz írt hozzászólások — mentéskor kerülnek fel */
  localComments: SupCommentVM[];
  ordSearch: string;
  ordResults: SupOrderResultVM[];
  /** levelezés-kereső: L-azonosító, tárgy, név vagy e-mail cím */
  mailSearch: string;
  mailResults: SupportMailThreadSummary[];
  labelFormOpen: boolean;
  newLabelName: string;
  newLabelColor: string;
}

/** GET orders?search= válasz egy eleme (csak a felhasznált mezők) */
interface OrderListItemDto {
  id?: number;
  receipt_id?: number;
  customer?: string;
  merchant_id?: number;
  etsy_shop_id?: number;
  is_archived?: number;
  source_key?: string;
  is_manual?: number;
  merchant?: { name?: string } | null;
}

/** Ha az oszlop-lista nem tölthető be, ezekkel indul a tábla. */
const DEFAULT_COLUMNS: SupportColumn[] = [
  { id: -1, name: 'Új', color: '#5E7FA8', position: 0, isDone: false, ticketCount: 0 },
  { id: -2, name: 'Folyamatban', color: '#C9A45C', position: 1, isDone: false, ticketCount: 0 },
  { id: -3, name: 'Várakozik', color: '#9A6E9E', position: 2, isDone: false, ticketCount: 0 },
  { id: -4, name: 'Megoldva', color: '#7FA25B', position: 3, isDone: true, ticketCount: 0 },
];

const PRIOS: Record<SupportTicketPriority, { label: string; sub: string; cls: string }> = {
  P1: { label: 'P1 · Kritikus', sub: '24 órán belül', cls: 'sup-prio--p1' },
  P2: { label: 'P2 · Standard', sub: '3 munkanapon belül', cls: 'sup-prio--p2' },
  P3: { label: 'P3 · Alacsony', sub: 'kapacitás szerint', cls: 'sup-prio--p3' },
};

const SHOPS = ['Etsy', 'Jump', 'Shopify', 'BGFox'];
const SHOP_COLORS: Record<string, string> = {
  Etsy: '#B9834A',
  Jump: '#5E7FA8',
  Shopify: '#6E9663',
  BGFox: '#9A6E9E',
};

/** SLA órában — P1: 24 óra, P2: 3 munkanap, P3: 7 nap (23-logic.js supSLA) */
const SUP_SLA: Record<SupportTicketPriority, number> = { P1: 24, P2: 72, P3: 168 };

const LABEL_SWATCHES = ['#5E7FA8', '#C97C7C', '#9A6E9E', '#8FB859', '#C9A45C', '#B9834A'];

/** Ismert kollégák avatár-színei a prototípusból + determinisztikus fallback paletta */
const USER_COLORS: Record<string, string> = {
  'Virág': '#C77B9B',
  'Bence': '#5E7FA8',
  'Peti': '#6E9663',
};

const AVATAR_PALETTE = ['#C77B9B', '#5E7FA8', '#6E9663', '#B9834A', '#9A6E9E', '#C9A45C'];

@Component({
  selector: 'app-support',
  templateUrl: './support.component.html',
  styleUrl: './support.component.scss'
})
export class SupportComponent implements OnInit, OnDestroy {
  readonly prioKeys: SupportTicketPriority[] = ['P1', 'P2', 'P3'];
  readonly prios = PRIOS;
  readonly shops = SHOPS;
  readonly shopColors = SHOP_COLORS;
  readonly labelSwatches = LABEL_SWATCHES;

  tickets: SupportTicket[] = [];
  labels: SupportLabel[] = [];
  /** kompakt kártyák: csak cím, határidő, prioritás, fejek (localStorage-ban marad) */
  compact = false;

  // Saját emlékeztetők
  reminders: SupportReminder[] = [];
  reminderVms: SupReminderVM[] = [];
  people: UserDirectoryEntry[] = [];
  remForm: SupReminderForm | null = null;
  remSaving = false;
  /** ?reminder=<id> — e-mailből érkezve ezt nyitjuk meg */
  private pendingReminderId: number | null = null;
  columns: SupportColumn[] = [];
  board: SupColumnVM[] = [];

  // Oszlopkezelés: colForm.id === null → új oszlop, egyébként a meglévő szerkesztése
  colForm: { id: number | null; name: string; color: string; isDone: boolean } | null = null;
  colSaving = false;
  colReordering = false;
  /** törlésre kijelölt oszlop + hova kerüljenek a taskjai */
  colDelete: { column: SupColumnVM; moveTo: number | null } | null = null;
  colDeleting = false;

  // Emlékeztetők panel (jobbról benyíló, mindent lefedő)
  remOpen = false;
  loading = true;

  modal: SupModalState | null = null;
  saving = false;
  lightboxUrl: string | null = null;
  /** nagyban megnyitott csatolmány fájlneve */
  lightboxFile: string | null = null;

  @ViewChild('titleInput') titleInput?: ElementRef<HTMLInputElement>;
  @ViewChild('boardEl') boardEl?: ElementRef<HTMLDivElement>;

  /** kép feltöltése folyamatban (a + gomb tiltásához) */
  uploadingImage = false;

  private dragTicketId: number | null = null;
  /** ?ticket=<id> query paramból megnyitandó ticket (Mai nap dashboard linkje) */
  private pendingTicketId: number | null = null;
  private readonly ordSearch$ = new Subject<string>();
  private ordSearchSub?: Subscription;
  private readonly mailSearch$ = new Subject<string>();
  private mailSearchSub?: Subscription;
  private queryParamSub?: Subscription;

  constructor(
    private supportService: SupportService,
    private http: HttpClient,
    private router: Router,
    private route: ActivatedRoute,
    private auth: AuthService,
    private confirmDialog: ConfirmDialogService,
    private notification: NotificationService,
    private directory: UserDirectoryService,
    private mailService: SupportMailService,
  ) {}

  ngOnInit(): void {
    try {
      this.compact = localStorage.getItem('sup.compact') === '1';
    } catch {
      this.compact = false;
    }
    this.load();
    // Mai nap dashboard: /support?ticket=<id> — betöltés után nyissuk a ticket modálját
    this.queryParamSub = this.route.queryParamMap.subscribe((params) => {
      const reminderRaw = params.get('reminder');
      if (reminderRaw !== null && Number.isFinite(Number(reminderRaw))) {
        this.pendingReminderId = Number(reminderRaw);
        this.tryOpenPendingReminder();
      }
      const raw = params.get('ticket');
      if (raw === null) {
        return;
      }
      const id = Number(raw);
      if (!Number.isFinite(id)) {
        return;
      }
      this.pendingTicketId = id;
      this.tryOpenPendingTicket();
    });
    this.ordSearchSub = this.ordSearch$
      .pipe(
        debounceTime(250),
        distinctUntilChanged(),
        switchMap((query) => {
          const q = query.trim();
          if (!q) {
            return of<OrderListItemDto[]>([]);
          }
          const params = new URLSearchParams();
          params.set('search', q);
          params.set('archived', 'all');
          params.set('page', '0');
          params.set('page_size', '4');
          return this.http.get<{ items: OrderListItemDto[] }>(`orders?${params.toString()}`).pipe(
            map((response) => response?.items || []),
            catchError(() => of<OrderListItemDto[]>([])),
          );
        }),
      )
      .subscribe((items) => {
        if (!this.modal) {
          return;
        }
        this.modal.ordResults = items.slice(0, 4).map((order) => {
          const shop = this.merchantName(order);
          return {
            ref: {
              orderId: order?.id ?? 0,
              displayId: `#${order?.receipt_id ?? ''}`,
              customerName: order?.customer || 'Ismeretlen vevő',
              shop,
            },
            name: order?.customer || 'Ismeretlen vevő',
            meta: `#${order?.receipt_id ?? ''} · ${shop}${order?.is_archived === 1 ? ' · archív' : ''}`,
          };
        });
      });
    this.mailSearchSub = this.mailSearch$
      .pipe(
        debounceTime(250),
        distinctUntilChanged(),
        switchMap((query) => {
          if (!query.trim()) {
            return of<SupportMailThreadSummary[]>([]);
          }
          return this.mailService.listThreads('all', query, 0, 5).pipe(
            map((response) => response?.items || []),
            catchError(() => of<SupportMailThreadSummary[]>([])),
          );
        }),
      )
      .subscribe((items) => {
        if (!this.modal) {
          return;
        }
        const linked = new Set(this.modal.draft.mailThreads.map((t) => t.id));
        this.modal.mailResults = items.filter((item) => !linked.has(item.id));
      });
  }

  ngOnDestroy(): void {
    this.ordSearchSub?.unsubscribe();
    this.mailSearchSub?.unsubscribe();
    this.queryParamSub?.unsubscribe();
  }

  /** A ?ticket= paramban kért ticket megnyitása, ha már betöltöttük a listát.
   *  Ha a ticket nem található, csendben ignoráljuk. */
  private tryOpenPendingTicket(): void {
    if (this.loading || this.pendingTicketId === null) {
      return;
    }
    const ticket = this.tickets.find((t) => t.id === this.pendingTicketId);
    this.pendingTicketId = null;
    if (ticket) {
      this.openTicketModal(ticket);
    }
  }

  // --------------------------------------------------------------------------
  // Betöltés + tábla építés
  // --------------------------------------------------------------------------

  private load(): void {
    this.loading = true;
    forkJoin({
      tickets: this.supportService.getTickets().pipe(catchError(() => of([] as SupportTicket[]))),
      labels: this.supportService.getLabels().pipe(catchError(() => of([] as SupportLabel[]))),
      reminders: this.supportService.getReminders().pipe(catchError(() => of([] as SupportReminder[]))),
      people: this.directory.list().pipe(catchError(() => of([] as UserDirectoryEntry[]))),
      columns: this.supportService.getColumns().pipe(catchError(() => of([] as SupportColumn[]))),
    }).subscribe(({ tickets, labels, reminders, people, columns }) => {
      this.tickets = tickets || [];
      this.labels = labels || [];
      this.reminders = reminders || [];
      this.people = people || [];
      this.columns = (columns && columns.length) ? columns : DEFAULT_COLUMNS;
      this.loading = false;
      this.rebuild();
      // Mobilon a tábla vízszintesen húzható (scroll-snap). Betöltéskor először csak az „Új oszlop" létezik,
      // a böngésző ahhoz tapadna, és az oszlopok megérkezésekor a tábla végére ugrana — vissza az elejére.
      setTimeout(() => this.boardEl?.nativeElement.scrollTo({ left: 0 }), 0);
      this.rebuildReminders();
      this.tryOpenPendingTicket();
      this.tryOpenPendingReminder();
    });
  }

  /** Kompakt ↔ részletes kártyák — az idő-információk (határidő, dátum) kompaktban is látszanak. */
  setCompact(value: boolean): void {
    this.compact = value;
    try {
      localStorage.setItem('sup.compact', value ? '1' : '0');
    } catch {
      // privát mód — nem baj, csak nem jegyzi meg
    }
  }

  // --------------------------------------------------------------------------
  // Saját emlékeztetők
  // --------------------------------------------------------------------------

  private rebuildReminders(): void {
    this.reminderVms = this.reminders.map((reminder) => {
      const due = reminder.dueAt ? new Date(reminder.dueAt).getTime() : NaN;
      const done = !!reminder.doneAt;
      return {
        reminder,
        done,
        deadline: !done && !isNaN(due) ? this.deadlineFromDue(due, 2) : null,
        dueStr: !isNaN(due) ? this.dueStrOf(due) : null,
        avatars: reminder.assignees.map((person) => this.avatarFor(person.name)),
      };
    });
  }

  private tryOpenPendingReminder(): void {
    if (this.loading || this.pendingReminderId === null) {
      return;
    }
    const reminder = this.reminders.find((r) => r.id === this.pendingReminderId);
    this.pendingReminderId = null;
    if (reminder) {
      this.remOpen = true;
      this.openReminderForm(reminder);
    }
  }

  openReminderForm(reminder?: SupportReminder): void {
    const me = this.meId();
    this.remForm = reminder
      ? {
          id: reminder.id,
          title: reminder.title,
          description: reminder.description || '',
          due: this.toLocalInput(reminder.dueAt),
          assigneeIds: reminder.assignees.map((p) => p.id),
        }
      : { id: null, title: '', description: '', due: '', assigneeIds: me ? [me] : [] };
  }

  cancelReminderForm(): void {
    this.remForm = null;
  }

  isReminderAssignee(userId: number): boolean {
    return !!this.remForm?.assigneeIds.includes(userId);
  }

  toggleReminderAssignee(userId: number): void {
    if (!this.remForm) {
      return;
    }
    const ids = this.remForm.assigneeIds;
    this.remForm.assigneeIds = ids.includes(userId) ? ids.filter((id) => id !== userId) : [...ids, userId];
  }

  saveReminder(): void {
    const form = this.remForm;
    if (!form || this.remSaving) {
      return;
    }
    if (!form.title.trim()) {
      this.notification.show('Az emlékeztető címe kötelező.', 'danger');
      return;
    }
    const dueIso = this.fromLocalInput(form.due);
    if (form.due && !dueIso) {
      this.notification.show('Érvénytelen határidő.', 'danger');
      return;
    }
    const payload: SaveSupportReminderRequest = {
      title: form.title.trim(),
      description: form.description.trim() || null,
      dueAt: dueIso,
      assigneeUserIds: [...form.assigneeIds],
    };
    this.remSaving = true;
    const request = form.id === null
      ? this.supportService.createReminder(payload)
      : this.supportService.updateReminder(form.id, payload);
    request.subscribe({
      next: (saved) => {
        this.remSaving = false;
        this.remForm = null;
        this.replaceReminder(saved);
        this.notification.show(form.id === null ? 'Emlékeztető létrehozva.' : 'Emlékeztető mentve.', 'success');
      },
      error: (err) => {
        this.remSaving = false;
        this.notification.show(this.errorText(err, 'Nem sikerült menteni az emlékeztetőt.'), 'danger');
      },
    });
  }

  toggleReminderDone(vm: SupReminderVM, event?: Event): void {
    event?.stopPropagation();
    this.supportService.updateReminder(vm.reminder.id, { done: !vm.done }).subscribe({
      next: (saved) => this.replaceReminder(saved),
      error: (err) => this.notification.show(this.errorText(err, 'Nem sikerült frissíteni az emlékeztetőt.'), 'danger'),
    });
  }

  deleteReminder(vm: SupReminderVM, event?: Event): void {
    event?.stopPropagation();
    this.confirmDialog
      .open({
        title: 'Emlékeztető törlése',
        message: `A(z) „${vm.reminder.title}” emlékeztető törlődik. Biztos?`,
        confirmLabel: 'Törlés',
        cancelLabel: 'Mégse',
        tone: 'danger',
      })
      .subscribe((result) => {
        if (!result?.confirmed) {
          return;
        }
        this.supportService.deleteReminder(vm.reminder.id).subscribe({
          next: () => {
            this.reminders = this.reminders.filter((r) => r.id !== vm.reminder.id);
            if (this.remForm?.id === vm.reminder.id) {
              this.remForm = null;
            }
            this.rebuildReminders();
          },
          error: (err) => this.notification.show(this.errorText(err, 'Nem sikerült törölni az emlékeztetőt.'), 'danger'),
        });
      });
  }

  private replaceReminder(saved: SupportReminder): void {
    const index = this.reminders.findIndex((r) => r.id === saved.id);
    if (index >= 0) {
      this.reminders[index] = saved;
    } else {
      this.reminders = [saved, ...this.reminders];
    }
    this.rebuildReminders();
  }

  get openReminderCount(): number {
    return this.reminderVms.filter((vm) => !vm.done).length;
  }

  personAvatar(person: UserDirectoryEntry): SupAvatarVM {
    return this.avatarFor(person.fullName || person.username);
  }

  private meId(): number | null {
    return this.auth.user?.id ?? null;
  }

  /** ISO → datetime-local ('YYYY-MM-DDTHH:mm', helyi idő) */
  private toLocalInput(iso: string | null): string {
    if (!iso) {
      return '';
    }
    const d = new Date(iso);
    if (isNaN(d.getTime())) {
      return '';
    }
    const pad = (n: number) => String(n).padStart(2, '0');
    return `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}T${pad(d.getHours())}:${pad(d.getMinutes())}`;
  }

  /** datetime-local → ISO; üres = null */
  private fromLocalInput(value: string): string | null {
    if (!value) {
      return null;
    }
    const d = new Date(value);
    return isNaN(d.getTime()) ? null : d.toISOString();
  }

  private dueStrOf(due: number): string {
    const d = new Date(due);
    const months = ['jan.', 'febr.', 'márc.', 'ápr.', 'máj.', 'jún.', 'júl.', 'aug.', 'szept.', 'okt.', 'nov.', 'dec.'];
    const pad = (n: number) => String(n).padStart(2, '0');
    return `${months[d.getMonth()]} ${d.getDate()}. ${pad(d.getHours())}:${pad(d.getMinutes())}`;
  }

  private rebuild(): void {
    const labelById = new Map(this.labels.map((label) => [label.id, label]));
    const columns = this.columns.length ? this.columns : DEFAULT_COLUMNS;
    const known = new Set(columns.map((c) => c.name));
    this.board = columns.map((col) => ({
      id: col.id,
      key: col.name,
      color: col.color,
      isDone: col.isDone,
      cards: this.tickets
        .filter((ticket) => ticket.status === col.name
          // Ismeretlen státuszú (törölt oszlopból maradt) task az első oszlopba kerül, hogy ne tűnjön el.
          || (!known.has(ticket.status) && col === columns[0]))
        .map((ticket) => this.buildCard(ticket, labelById)),
    }));
  }

  // --------------------------------------------------------------------------
  // Oszlopok: létrehozás / törlés (átmozgatással)
  // --------------------------------------------------------------------------

  openColumnForm(): void {
    this.colForm = { id: null, name: '', color: LABEL_SWATCHES[0], isDone: false };
  }

  /** Meglévő oszlop szerkesztése (név, szín, „kész") — a fejlécben nyílik meg. */
  editColumn(column: SupColumnVM, event?: Event): void {
    event?.stopPropagation();
    this.colDelete = null;
    this.colForm = { id: column.id, name: column.key, color: column.color, isDone: column.isDone };
  }

  /** Oszlop mozgatása balra/jobbra; a sorrend a szerveren is megmarad. */
  moveColumn(column: SupColumnVM, dir: -1 | 1, event?: Event): void {
    event?.stopPropagation();
    if (this.colReordering) {
      return;
    }
    const index = this.board.indexOf(column);
    const target = index + dir;
    if (index < 0 || target < 0 || target >= this.board.length) {
      return;
    }
    const ordered = [...this.board];
    ordered.splice(index, 1);
    ordered.splice(target, 0, column);
    this.board = ordered;
    this.colReordering = true;
    this.supportService.reorderColumns(ordered.map((c) => c.id)).subscribe({
      next: (columns) => {
        this.colReordering = false;
        this.columns = (columns && columns.length) ? columns : DEFAULT_COLUMNS;
        this.rebuild();
      },
      error: (err) => {
        this.colReordering = false;
        this.notification.show(this.errorText(err, 'Nem sikerült menteni az oszlopok sorrendjét.'), 'danger');
        this.rebuild();
      },
    });
  }

  cancelColumnForm(): void {
    this.colForm = null;
  }

  saveColumn(): void {
    const form = this.colForm;
    if (!form || this.colSaving) {
      return;
    }
    if (!form.name.trim()) {
      this.notification.show('Az oszlop neve kötelező.', 'danger');
      return;
    }
    this.colSaving = true;
    const payload = { name: form.name.trim(), color: form.color, isDone: form.isDone };
    const isEdit = form.id !== null;
    const request$ = isEdit
      ? this.supportService.updateColumn(form.id as number, payload)
      : this.supportService.createColumn(payload);
    request$.subscribe({
      next: () => {
        this.colSaving = false;
        this.colForm = null;
        this.notification.show(isEdit ? 'Oszlop mentve.' : 'Oszlop létrehozva.', 'success');
        // Átnevezésnél a ticketek státusza is változik — teljes újratöltés.
        if (isEdit) {
          this.load();
        } else {
          this.reloadColumns();
        }
      },
      error: (err) => {
        this.colSaving = false;
        this.notification.show(this.errorText(err, isEdit ? 'Nem sikerült menteni az oszlopot.' : 'Nem sikerült létrehozni az oszlopot.'), 'danger');
      },
    });
  }

  askDeleteColumn(column: SupColumnVM, event?: Event): void {
    event?.stopPropagation();
    this.colForm = null;
    const others = this.otherColumns(column);
    this.colDelete = { column, moveTo: others[0]?.id ?? null };
  }

  cancelDeleteColumn(): void {
    this.colDelete = null;
  }

  otherColumns(column: SupColumnVM): SupColumnVM[] {
    return this.board.filter((c) => c.id !== column.id);
  }

  confirmDeleteColumn(): void {
    const pending = this.colDelete;
    if (!pending || this.colDeleting) {
      return;
    }
    const hasCards = pending.column.cards.length > 0;
    if (hasCards && !pending.moveTo) {
      this.notification.show('Válaszd ki, melyik oszlopba kerüljenek a taskok.', 'warning');
      return;
    }
    this.colDeleting = true;
    this.supportService.deleteColumn(pending.column.id, hasCards ? pending.moveTo : null).subscribe({
      next: (result) => {
        this.colDeleting = false;
        this.colDelete = null;
        this.notification.show(result.moved
          ? `Oszlop törölve, ${result.moved} task átmozgatva.`
          : 'Oszlop törölve.', 'success');
        this.load();
      },
      error: (err) => {
        this.colDeleting = false;
        this.notification.show(this.errorText(err, 'Nem sikerült törölni az oszlopot.'), 'danger');
      },
    });
  }

  private reloadColumns(): void {
    this.supportService.getColumns().pipe(catchError(() => of([] as SupportColumn[]))).subscribe((columns) => {
      this.columns = (columns && columns.length) ? columns : DEFAULT_COLUMNS;
      this.rebuild();
    });
  }

  /** „Kész" oszlop-e egy státusz (nincs élő határidő). */
  private isDoneStatus(status: SupportTicketStatus): boolean {
    const columns = this.columns.length ? this.columns : DEFAULT_COLUMNS;
    return !!columns.find((c) => c.name === status)?.isDone;
  }

  // --------------------------------------------------------------------------
  // Emlékeztetők fiók
  // --------------------------------------------------------------------------

  toggleRem(): void {
    this.remOpen = !this.remOpen;
  }

  closeRem(): void {
    this.remOpen = false;
  }

  @HostListener('document:keydown.escape')
  onEscape(): void {
    if (this.remOpen && !this.modal) {
      this.closeRem();
    }
  }

  /** A csukott fiók kis infója: a legközelebbi határidő. */
  get remNext(): SupReminderVM | null {
    return this.reminderVms.find((vm) => !vm.done && vm.deadline) || null;
  }

  /** Van-e lejárt (piros) emlékeztető — a csukott fiókon pont jelzi. */
  get remHasOverdue(): boolean {
    return this.reminderVms.some((vm) => !vm.done && vm.deadline?.overdue);
  }

  private buildCard(ticket: SupportTicket, labelById: Map<number, SupportLabel>): SupCardVM {
    const commenters = [...new Set((ticket.comments || []).map((c) => c.author))]
      .filter((who) => who !== ticket.creator);
    // Fejek: először a felelősök, utána a létrehozó és a hozzászólók (név szerint egyszer).
    const names: string[] = [];
    [...(ticket.assignees || []).map((p) => p.name), ticket.creator, ...commenters].forEach((who) => {
      if (who && !names.includes(who)) {
        names.push(who);
      }
    });
    const desc = ticket.description || '';
    return {
      ticket,
      title: ticket.title,
      excerpt: desc.length > 90 ? `${desc.slice(0, 90)}…` : desc,
      shopName: ticket.shop || null,
      shopColor: SHOP_COLORS[ticket.shop || ''] || '#707870',
      labels: (ticket.labelIds || [])
        .map((id) => labelById.get(id))
        .filter((label): label is SupportLabel => !!label),
      orderLabel: ticket.order ? `${ticket.order.customerName} · ${ticket.order.displayId}` : null,
      deadline: this.deadlineFor(ticket.createdAt, ticket.priority, ticket.isDone || this.isDoneStatus(ticket.status)),
      prioLabel: PRIOS[ticket.priority]?.label || ticket.priority,
      prioClass: PRIOS[ticket.priority]?.cls || 'sup-prio--p3',
      dateLabel: this.dateLabel(ticket.createdAt),
      commentCount: (ticket.comments || []).length,
      avatars: names.map((who) => this.avatarFor(who)),
    };
  }

  // --------------------------------------------------------------------------
  // SLA határidő (23-logic.js supDeadline, valós „most” idővel)
  // --------------------------------------------------------------------------

  deadlineFor(
    createdAt: string | null,
    priority: SupportTicketPriority,
    done: boolean,
  ): SupDeadlineVM | null {
    // „Kész" oszlopban lévő taskoknak nincs aktív határidő
    if (!createdAt || done) {
      return null;
    }
    const created = new Date(createdAt).getTime();
    if (isNaN(created)) {
      return null;
    }
    const hours = SUP_SLA[priority] || 72;
    const due = created + hours * 3600 * 1000;
    return this.deadlineFromDue(due, hours * 0.34);
  }

  /** Határidő-címke tetszőleges időpontra; `soonHours` alatt sárga, lejárva piros. */
  private deadlineFromDue(due: number, soonHours: number): SupDeadlineVM {
    const diffH = (due - Date.now()) / 3600000;
    const overdue = diffH < 0;
    const absH = Math.abs(diffH);
    let text: string;
    let overdueText: string;
    if (absH < 24) {
      const h = Math.max(1, Math.round(absH));
      text = `${h} óra`;
      overdueText = `${h} órája`;
    } else {
      const d = Math.round(absH / 24);
      text = `${d} nap`;
      overdueText = `${d} napja`;
    }
    const dueDate = new Date(due);
    const pad = (n: number) => String(n).padStart(2, '0');
    const dueStr = `${pad(dueDate.getMonth() + 1)}.${pad(dueDate.getDate())}. ${pad(dueDate.getHours())}:${pad(dueDate.getMinutes())}`;
    const soon = !overdue && diffH < soonHours;
    return {
      overdue,
      soon,
      label: overdue ? `Lejárt ${overdueText}` : `${text} van hátra`,
      dueStr,
      color: overdue ? '#A14D4D' : (soon ? '#B77A28' : '#4C7A2E'),
      bg: overdue ? '#F6E7E7' : (soon ? '#F5EBD6' : '#EEF3E4'),
    };
  }

  get modalDeadline(): SupDeadlineVM | null {
    if (!this.modal) {
      return null;
    }
    return this.deadlineFor(this.modal.createdAt, this.modal.draft.priority, this.isDoneStatus(this.modal.draft.status));
  }

  get modalSlaText(): string {
    const priority = this.modal?.draft.priority || 'P2';
    const hours = SUP_SLA[priority] || 72;
    return `SLA: ${hours === 24 ? '24 óra' : `${hours / 24} munkanap`}`;
  }

  // --------------------------------------------------------------------------
  // Drag & drop az oszlopok között
  // --------------------------------------------------------------------------

  onDragStart(card: SupCardVM): void {
    this.dragTicketId = card.ticket.id;
  }

  onDragOver(event: DragEvent): void {
    event.preventDefault();
  }

  onDrop(column: SupColumnVM): void {
    const id = this.dragTicketId;
    this.dragTicketId = null;
    if (id === null) {
      return;
    }
    const ticket = this.tickets.find((t) => t.id === id);
    if (!ticket || ticket.status === column.key) {
      return;
    }
    const previousStatus = ticket.status;
    ticket.status = column.key;
    this.rebuild();
    this.supportService.updateTicket(id, { status: column.key }).subscribe({
      next: (updated) => this.replaceTicket(updated),
      error: () => {
        ticket.status = previousStatus;
        this.rebuild();
        this.notification.show('Nem sikerült átmozgatni a taskot — próbáld újra.', 'danger');
      },
    });
  }

  // --------------------------------------------------------------------------
  // Modál nyitás / zárás
  // --------------------------------------------------------------------------

  openTicket(card: SupCardVM): void {
    this.openTicketModal(card.ticket);
  }

  private openTicketModal(ticket: SupportTicket): void {
    // A beszélgetést megnézte → a zöld „N új" jelzés lenullázódik (a szerveren is)
    if (ticket.unreadComments > 0) {
      ticket.unreadComments = 0;
      ticket.unreadCommentAuthors = [];
      this.supportService.markTicketSeen(ticket.id).subscribe({ error: () => undefined });
      this.rebuild();
    }
    this.modal = {
      ticketId: ticket.id,
      isNew: false,
      draft: {
        title: ticket.title,
        description: ticket.description || '',
        status: ticket.status,
        priority: ticket.priority,
        shop: ticket.shop || null,
        order: ticket.order ? { ...ticket.order } : null,
        labelIds: [...(ticket.labelIds || [])],
        assigneeIds: (ticket.assignees || []).map((p) => p.id),
        mailThreads: [...(ticket.mailThreads || [])],
        attachments: [...(ticket.attachments || [])],
        pendingImages: [],
      },
      creator: ticket.creator,
      createdAt: ticket.createdAt,
      metaLine: `${ticket.displayId || 'T-' + ticket.id} · ${this.metaLine(ticket.creator, ticket.createdAt)}`,
      creatorAvatar: this.avatarFor(ticket.creator),
      comment: '',
      localComments: [],
      ordSearch: '',
      ordResults: [],
      mailSearch: '',
      mailResults: [],
      labelFormOpen: false,
      newLabelName: '',
      newLabelColor: LABEL_SWATCHES[0],
    };
  }

  addTask(column: SupColumnVM): void {
    const me = this.meName();
    this.modal = {
      ticketId: null,
      isNew: true,
      draft: {
        title: '',
        description: '',
        status: column.key,
        priority: 'P2',
        shop: null,
        order: null,
        labelIds: [],
        assigneeIds: [],
        mailThreads: [],
        attachments: [],
        pendingImages: [],
      },
      creator: me,
      createdAt: null,
      metaLine: this.metaLine(me, new Date().toISOString()),
      creatorAvatar: this.avatarFor(me),
      comment: '',
      localComments: [],
      ordSearch: '',
      ordResults: [],
      mailSearch: '',
      mailResults: [],
      labelFormOpen: false,
      newLabelName: '',
      newLabelColor: LABEL_SWATCHES[0],
    };
  }

  /**
   * Bezárás az X gombbal: ha van módosítás, megkérdezzük, mentse-e — így
   * félrekattintással/véletlen bezárással nem vész el a szerkesztés.
   */
  closeModal(): void {
    if (!this.modal || this.saving) {
      return;
    }
    if (!this.hasUnsavedChanges()) {
      this.discardModal();
      return;
    }
    this.confirmDialog.open({
      title: 'Mented a módosításokat?',
      message: 'A szerkesztésben mentetlen változások vannak. Mentsük őket, vagy kilépsz mentés nélkül?',
      tone: 'warning',
      confirmLabel: 'Mentés',
      cancelLabel: 'Kilépés mentés nélkül',
    }).subscribe((result) => {
      if (result?.confirmed) {
        this.save();
      } else if (result) {
        // A „Kilépés mentés nélkül" gomb — a dialógus bezárása (ESC) nem zár modált
        this.discardModal();
      }
    });
  }

  /** Modál eldobása mentés nélkül (a helyi előnézetek felszabadításával). */
  private discardModal(): void {
    (this.modal?.draft.pendingImages || []).forEach((p) => URL.revokeObjectURL(p.previewUrl));
    this.modal = null;
  }

  /** Van-e mentetlen módosítás a nyitott modálban? */
  private hasUnsavedChanges(): boolean {
    const modal = this.modal;
    if (!modal) {
      return false;
    }
    const draft = modal.draft;
    if (modal.isNew) {
      return !!(draft.title.trim() || draft.description.trim() || draft.order || draft.mailThreads.length
        || draft.shop || draft.labelIds.length || draft.assigneeIds.length || draft.pendingImages.length
        || modal.localComments.length || modal.comment.trim());
    }
    const ticket = this.tickets.find((t) => t.id === modal.ticketId);
    if (!ticket) {
      return false;
    }
    const sameLabels = draft.labelIds.length === (ticket.labelIds || []).length
      && draft.labelIds.every((id) => (ticket.labelIds || []).includes(id));
    const ticketAssignees = (ticket.assignees || []).map((p) => p.id);
    const sameAssignees = draft.assigneeIds.length === ticketAssignees.length
      && draft.assigneeIds.every((id) => ticketAssignees.includes(id));
    const ticketThreads = (ticket.mailThreads || []).map((t) => t.id);
    const sameThreads = draft.mailThreads.length === ticketThreads.length
      && draft.mailThreads.every((t) => ticketThreads.includes(t.id));
    return draft.title !== ticket.title
      || !sameThreads
      || draft.description !== (ticket.description || '')
      || draft.status !== ticket.status
      || draft.priority !== ticket.priority
      || (draft.shop || null) !== (ticket.shop || null)
      || (draft.order?.orderId ?? null) !== (ticket.order?.orderId ?? null)
      || !sameLabels
      || !sameAssignees
      || !!modal.comment.trim();
  }

  // --------------------------------------------------------------------------
  // Mentés / törlés
  // --------------------------------------------------------------------------

  save(): void {
    const modal = this.modal;
    if (!modal || this.saving) {
      return;
    }
    const draft = modal.draft;
    if (!draft.title.trim()) {
      // A cím kötelező — a szerver is elutasítaná, itt kapja meg a fókuszt
      this.notification.show('A task címe kötelező.', 'danger');
      this.titleInput?.nativeElement.focus();
      return;
    }
    const payload: UpdateSupportTicketRequest = {
      title: draft.title,
      description: draft.description,
      status: draft.status,
      priority: draft.priority,
      shop: draft.shop,
      orderId: draft.order ? draft.order.orderId : null,
      labelIds: [...draft.labelIds],
      assigneeUserIds: [...draft.assigneeIds],
      mailThreadIds: draft.mailThreads.map((t) => t.id),
    };
    this.saving = true;
    if (modal.isNew) {
      const pendingComments = modal.localComments.map((c) => c.text);
      this.supportService.createTicket({ ...payload, title: draft.title }).subscribe({
        next: (created: SupportTicket) => {
          if (pendingComments.length === 0) {
            this.onSaved(created);
            return;
          }
          // A mentés előtt írt hozzászólások sorban felkerülnek az új taskra
          from(pendingComments)
            .pipe(
              concatMap((text) => this.supportService.addComment(created.id, text)),
              toArray(),
              catchError(() => of([])),
            )
            .subscribe((comments) => {
              created.comments = [...(created.comments || []), ...comments];
              this.onSaved(created);
            });
        },
        error: (err) => this.onSaveError(err),
      });
    } else if (modal.ticketId !== null) {
      this.supportService.updateTicket(modal.ticketId, payload).subscribe({
        next: (updated) => this.onSaved(updated),
        error: (err) => this.onSaveError(err),
      });
    }
  }

  private onSaved(ticket: SupportTicket): void {
    const pending = this.modal?.draft.pendingImages || [];
    if (pending.length && ticket.id) {
      // Új taskhoz a mentés előtt kiválasztott képek most kerülnek fel.
      from(pending)
        .pipe(
          concatMap((p) => this.supportService.addAttachment(ticket.id, p.file)),
          toArray(),
          catchError(() => of([] as { attachments: string[] }[])),
        )
        .subscribe((results) => {
          const last = results[results.length - 1];
          if (last?.attachments) {
            ticket.attachments = last.attachments;
          }
          pending.forEach((p) => URL.revokeObjectURL(p.previewUrl));
          this.saving = false;
          this.replaceTicket(ticket);
          this.modal = null;
        });
      return;
    }
    this.saving = false;
    this.replaceTicket(ticket);
    this.modal = null;
  }

  private onSaveError(err?: unknown): void {
    this.saving = false;
    this.notification.show(this.errorText(err, 'Nem sikerült menteni a taskot — próbáld újra.'), 'danger');
  }

  /** A szerver magyar hibaüzenetét mutatjuk, ha van; különben az általános szöveget. */
  private errorText(err: unknown, fallback: string): string {
    const message = (err as { error?: { message?: string | string[] } })?.error?.message;
    if (Array.isArray(message) && message.length) {
      return message.join(' ');
    }
    if (typeof message === 'string' && message.trim()) {
      return message;
    }
    return fallback;
  }

  private replaceTicket(ticket: SupportTicket): void {
    const index = this.tickets.findIndex((t) => t.id === ticket.id);
    if (index >= 0) {
      this.tickets[index] = ticket;
    } else {
      this.tickets = [ticket, ...this.tickets];
    }
    this.rebuild();
  }

  deleteTask(): void {
    const modal = this.modal;
    if (!modal) {
      return;
    }
    if (modal.isNew) {
      // Még nem mentett task — nincs mit törölni a szerverről
      this.modal = null;
      return;
    }
    const title = modal.draft.title.trim() || 'névtelen';
    this.confirmDialog
      .open({
        title: 'Task törlése',
        message: `A(z) „${title}” task véglegesen törlődik a tábláról. Biztos törlöd?`,
        confirmLabel: 'Törlés',
        cancelLabel: 'Mégse',
        tone: 'danger',
      })
      .subscribe((result) => {
        if (!result?.confirmed || modal.ticketId === null) {
          return;
        }
        this.supportService.deleteTicket(modal.ticketId).subscribe({
          next: () => {
            this.tickets = this.tickets.filter((t) => t.id !== modal.ticketId);
            this.rebuild();
            this.modal = null;
            this.notification.show('A task törölve.', 'success');
          },
          error: (err) => this.notification.show(this.errorText(err, 'Nem sikerült törölni a taskot — próbáld újra.'), 'danger'),
        });
      });
  }

  // --------------------------------------------------------------------------
  // Állapot / prioritás / webshop / címkék
  // --------------------------------------------------------------------------

  pickStatus(status: SupportTicketStatus): void {
    if (this.modal) {
      this.modal.draft.status = status;
    }
  }

  pickPriority(priority: SupportTicketPriority): void {
    if (this.modal) {
      this.modal.draft.priority = priority;
    }
  }

  toggleShop(shop: string): void {
    if (this.modal) {
      this.modal.draft.shop = this.modal.draft.shop === shop ? null : shop;
    }
  }

  toggleLabel(label: SupportLabel): void {
    if (!this.modal) {
      return;
    }
    const ids = this.modal.draft.labelIds;
    this.modal.draft.labelIds = ids.includes(label.id)
      ? ids.filter((id) => id !== label.id)
      : [...ids, label.id];
  }

  isLabelOn(label: SupportLabel): boolean {
    return !!this.modal?.draft.labelIds.includes(label.id);
  }

  isAssignee(userId: number): boolean {
    return !!this.modal?.draft.assigneeIds.includes(userId);
  }

  toggleAssignee(userId: number): void {
    if (!this.modal) {
      return;
    }
    const ids = this.modal.draft.assigneeIds;
    this.modal.draft.assigneeIds = ids.includes(userId) ? ids.filter((id) => id !== userId) : [...ids, userId];
  }

  get modalAssigneeNames(): string {
    const ids = this.modal?.draft.assigneeIds || [];
    return this.people.filter((p) => ids.includes(p.id)).map((p) => p.fullName).join(', ');
  }

  openLabelForm(): void {
    if (this.modal) {
      this.modal.labelFormOpen = true;
      this.modal.newLabelName = '';
    }
  }

  pickSwatch(color: string): void {
    if (this.modal) {
      this.modal.newLabelColor = color;
    }
  }

  createLabel(): void {
    const modal = this.modal;
    if (!modal) {
      return;
    }
    const name = modal.newLabelName.trim();
    if (!name) {
      this.notification.show('A címke neve nem lehet üres.', 'danger');
      return;
    }
    this.supportService.createLabel({ name, color: modal.newLabelColor }).subscribe({
      next: (label) => {
        this.labels = [...this.labels, label];
        modal.draft.labelIds = [...modal.draft.labelIds, label.id];
        modal.labelFormOpen = false;
        modal.newLabelName = '';
        this.rebuild();
      },
      error: (err) => this.notification.show(this.errorText(err, 'Nem sikerült létrehozni a címkét — próbáld újra.'), 'danger'),
    });
  }

  /** Címke végleges törlése — minden taskról lekerül (a taskok megmaradnak). */
  deleteLabel(label: SupportLabel, event: Event): void {
    event.stopPropagation();
    const usedBy = this.tickets.filter((ticket) => (ticket.labelIds || []).includes(label.id)).length;
    const usage = usedBy > 0 ? ` Jelenleg ${usedBy} taskon szerepel, azokról lekerül.` : '';
    this.confirmDialog
      .open({
        title: 'Címke törlése',
        message: `A(z) „${label.name}” címke véglegesen törlődik.${usage} Biztos törlöd?`,
        confirmLabel: 'Törlés',
        cancelLabel: 'Mégse',
        tone: 'danger',
      })
      .subscribe((result) => {
        if (!result?.confirmed) {
          return;
        }
        this.supportService.deleteLabel(label.id).subscribe({
          next: () => {
            this.labels = this.labels.filter((item) => item.id !== label.id);
            this.tickets = this.tickets.map((ticket) => ({
              ...ticket,
              labelIds: (ticket.labelIds || []).filter((id) => id !== label.id),
            }));
            if (this.modal) {
              this.modal.draft.labelIds = this.modal.draft.labelIds.filter((id) => id !== label.id);
            }
            this.rebuild();
            this.notification.show('A címke törölve.', 'success');
          },
          error: (err) => this.notification.show(this.errorText(err, 'Nem sikerült törölni a címkét — próbáld újra.'), 'danger'),
        });
      });
  }

  // --------------------------------------------------------------------------
  // Hozzászólások — azonnal mennek (a mentéstől függetlenül)
  // --------------------------------------------------------------------------

  get modalComments(): SupCommentVM[] {
    const modal = this.modal;
    if (!modal) {
      return [];
    }
    if (modal.isNew) {
      return modal.localComments;
    }
    const ticket = this.tickets.find((t) => t.id === modal.ticketId);
    const myId = this.auth.user?.id ?? null;
    return (ticket?.comments || []).map((c) => ({
      id: c.id,
      who: c.author,
      text: c.text,
      time: this.timeLabel(c.createdAt),
      avatar: this.avatarFor(c.author),
      canDelete: myId != null && c.authorUserId === myId,
    }));
  }

  sendComment(): void {
    const modal = this.modal;
    if (!modal) {
      return;
    }
    const text = modal.comment.trim();
    if (!text) {
      return;
    }
    if (modal.isNew) {
      // Új, még nem mentett task — a hozzászólás mentéskor kerül fel a szerverre
      modal.localComments = [
        ...modal.localComments,
        {
          id: null,
          who: this.meName(),
          text,
          time: this.timeLabel(new Date().toISOString()),
          avatar: this.avatarFor(this.meName()),
          // Még nincs szerveren — a helyi listából a mentésig nem törlünk.
          canDelete: false,
        },
      ];
      modal.comment = '';
      return;
    }
    if (modal.ticketId === null) {
      return;
    }
    this.supportService.addComment(modal.ticketId, text).subscribe({
      next: (comment) => {
        const ticket = this.tickets.find((t) => t.id === modal.ticketId);
        if (ticket) {
          ticket.comments = [...(ticket.comments || []), comment];
          this.rebuild();
        }
      },
      error: () => this.notification.show('Nem sikerült elküldeni a hozzászólást — próbáld újra.', 'danger'),
    });
    modal.comment = '';
  }

  onCommentKeydown(event: Event): void {
    event.preventDefault();
    this.sendComment();
  }

  // --------------------------------------------------------------------------
  // Kapcsolt rendelés
  // --------------------------------------------------------------------------

  onOrderSearchChange(value: string): void {
    if (!this.modal) {
      return;
    }
    this.modal.ordSearch = value;
    if (!value.trim()) {
      this.modal.ordResults = [];
    }
    this.ordSearch$.next(value);
  }

  pickOrder(result: SupOrderResultVM): void {
    if (!this.modal) {
      return;
    }
    this.modal.draft.order = { ...result.ref };
    this.modal.ordSearch = '';
    this.modal.ordResults = [];
  }

  removeOrder(): void {
    if (this.modal) {
      this.modal.draft.order = null;
    }
  }

  // --------------------------------------------------------------------------
  // Kapcsolt levelezés
  // --------------------------------------------------------------------------

  /** A levelezés csak annak látszik a tasknál, aki a Levelezés oldalt is láthatja. */
  get canSeeMail(): boolean {
    return userHasMenu(this.auth.user, MENU_MAIL);
  }

  onMailSearchChange(value: string): void {
    if (!this.modal) {
      return;
    }
    this.modal.mailSearch = value;
    if (!value.trim()) {
      this.modal.mailResults = [];
    }
    this.mailSearch$.next(value);
  }

  pickMailThread(thread: SupportMailThreadSummary): void {
    if (!this.modal || this.modal.draft.mailThreads.some((t) => t.id === thread.id)) {
      return;
    }
    this.modal.draft.mailThreads = [...this.modal.draft.mailThreads, {
      id: thread.id,
      displayId: thread.displayId,
      subject: thread.subject,
      customerName: thread.customerName,
      customerEmail: thread.customerEmail,
      status: thread.status,
      unread: thread.unread,
      lastMessageAt: thread.lastMessageAt,
    }];
    this.modal.mailSearch = '';
    this.modal.mailResults = [];
    this.mailSearch$.next('');
  }

  removeMailThread(thread: SupportTicketMailThreadRef): void {
    if (!this.modal) {
      return;
    }
    this.modal.draft.mailThreads = this.modal.draft.mailThreads.filter((t) => t.id !== thread.id);
  }

  openMailThread(thread: SupportTicketMailThreadRef): void {
    this.router.navigate(['/support/levelezes'], { queryParams: { thread: thread.id } });
  }

  openOrder(): void {
    const order = this.modal?.draft.order;
    if (!order) {
      return;
    }
    this.modal = null;
    this.router.navigate(['/orders', order.orderId]);
  }

  orderLabelOf(order: SupportTicketOrderRef): string {
    return `${order.customerName} · ${order.displayId} · ${order.shop}`;
  }

  // --------------------------------------------------------------------------
  // Csatolt képek (placeholder slotok — feltöltés API még nincs a kontraktban)
  // --------------------------------------------------------------------------

  /** Feltöltött (mentett) csatolmányok a nyitott taskon. */
  get modalAttachments(): string[] {
    return this.modal?.draft.attachments || [];
  }

  /** Még nem mentett (új task) képek előnézete. */
  get modalPendingImages(): { file: File; previewUrl: string }[] {
    return this.modal?.draft.pendingImages || [];
  }

  /** Legalább 3 üres hely a rácsban, hogy a design ne ugráljon. */
  get emptyImgSlots(): number[] {
    const used = this.modalAttachments.length + this.modalPendingImages.length;
    return Array.from({ length: Math.max(0, 3 - used) }, (_, i) => i);
  }

  /** Kép kiválasztása: mentett taskon azonnal feltölt, újnál a mentésig vár. */
  onAttachmentSelected(event: Event): void {
    const input = event.target as HTMLInputElement;
    const files = Array.from(input.files || []);
    input.value = '';
    if (!this.modal || !files.length) {
      return;
    }
    for (const file of files) {
      if (!file.type.startsWith('image/')) {
        this.notification.show('Csak képfájl tölthető fel.', 'danger');
        continue;
      }
      if (file.size > 10 * 1024 * 1024) {
        this.notification.show('A kép legfeljebb 10 MB lehet.', 'danger');
        continue;
      }
      const ticketId = this.modal.ticketId;
      if (ticketId) {
        this.uploadingImage = true;
        this.supportService.addAttachment(ticketId, file).subscribe({
          next: (res) => {
            this.uploadingImage = false;
            if (this.modal) {
              this.modal.draft.attachments = res.attachments || [];
            }
            const ticket = this.tickets.find((t) => t.id === ticketId);
            if (ticket) {
              ticket.attachments = res.attachments || [];
            }
            this.notification.show('Kép feltöltve!', 'success');
          },
          error: (e) => {
            this.uploadingImage = false;
            this.notification.show(e?.error?.message || 'A kép feltöltése nem sikerült!', 'danger');
          },
        });
      } else {
        this.modal.draft.pendingImages = [
          ...this.modal.draft.pendingImages,
          { file, previewUrl: URL.createObjectURL(file) },
        ];
      }
    }
  }

  /** Mentett csatolmány törlése (X gomb). */
  removeAttachment(fileName: string): void {
    const modal = this.modal;
    const ticketId = modal?.ticketId;
    if (!modal || !ticketId) {
      return;
    }
    this.supportService.removeAttachment(ticketId, fileName).subscribe({
      next: (res) => {
        if (this.modal) {
          this.modal.draft.attachments = res.attachments || [];
        }
        const ticket = this.tickets.find((t) => t.id === ticketId);
        if (ticket) {
          ticket.attachments = res.attachments || [];
        }
        this.notification.show('Kép törölve.', 'success');
      },
      error: (e) => this.notification.show(e?.error?.message || 'A kép törlése nem sikerült!', 'danger'),
    });
  }

  /** Még nem mentett kép elvetése (X gomb). */
  removePendingImage(index: number): void {
    if (!this.modal) {
      return;
    }
    const pending = this.modal.draft.pendingImages[index];
    if (pending) {
      URL.revokeObjectURL(pending.previewUrl);
    }
    this.modal.draft.pendingImages = this.modal.draft.pendingImages.filter((_, i) => i !== index);
  }

  /** Hozzászólás törlése — a szerver is ellenőrzi, hogy a szerző kéri-e. */
  deleteComment(comment: SupCommentVM): void {
    const modal = this.modal;
    const ticketId = modal?.ticketId;
    if (!modal || !ticketId || comment.id == null) {
      return;
    }
    this.confirmDialog.open({
      title: 'Hozzászólás törlése?',
      message: 'A hozzászólás véglegesen törlődik a beszélgetésből.',
      tone: 'danger',
      confirmLabel: 'Törlés',
      cancelLabel: 'Mégse',
    }).subscribe((result) => {
      if (!result?.confirmed) {
        return;
      }
      this.supportService.deleteComment(ticketId, comment.id as number).subscribe({
        next: () => {
          const ticket = this.tickets.find((t) => t.id === ticketId);
          if (ticket) {
            this.replaceTicket({
              ...ticket,
              comments: (ticket.comments || []).filter((c) => c.id !== comment.id),
            });
          }
          this.notification.show('Hozzászólás törölve.', 'success');
        },
        error: (e) => this.notification.show(e?.error?.message || 'A törlés nem sikerült!', 'danger'),
      });
    });
  }

  /** Csatolmány nagyban (fájlnév alapján, auth-fejléces betöltéssel). */
  openLightbox(fileName: string): void {
    this.lightboxFile = fileName;
  }

  closeLightbox(): void {
    this.lightboxUrl = null;
    this.lightboxFile = null;
  }

  // --------------------------------------------------------------------------
  // Segédek
  // --------------------------------------------------------------------------

  trackColumn(_: number, column: SupColumnVM): string {
    return column.key;
  }

  trackCard(_: number, card: SupCardVM): number {
    return card.ticket.id;
  }

  private meName(): string {
    return this.auth.user?.full_name || this.auth.user?.username || 'Én';
  }

  private avatarFor(name: string): SupAvatarVM {
    return {
      initial: (name || '?').charAt(0).toUpperCase(),
      color: this.userColor(name),
      title: name,
    };
  }

  private userColor(name: string): string {
    if (USER_COLORS[name]) {
      return USER_COLORS[name];
    }
    if (!name) {
      return '#9AA098';
    }
    let hash = 0;
    for (let i = 0; i < name.length; i++) {
      hash = (hash * 31 + name.charCodeAt(i)) | 0;
    }
    return AVATAR_PALETTE[Math.abs(hash) % AVATAR_PALETTE.length];
  }

  private dateLabel(iso: string | null): string {
    if (!iso) {
      return '';
    }
    const date = new Date(iso);
    if (isNaN(date.getTime())) {
      return '';
    }
    const pad = (n: number) => String(n).padStart(2, '0');
    return `${pad(date.getMonth() + 1)}.${pad(date.getDate())}`;
  }

  private timeLabel(iso: string): string {
    const date = new Date(iso);
    if (isNaN(date.getTime())) {
      return '';
    }
    const pad = (n: number) => String(n).padStart(2, '0');
    return `${pad(date.getMonth() + 1)}.${pad(date.getDate())} ${pad(date.getHours())}:${pad(date.getMinutes())}`;
  }

  private metaLine(creator: string, iso: string | null): string {
    const date = iso ? new Date(iso) : new Date();
    const pad = (n: number) => String(n).padStart(2, '0');
    const dateStr = isNaN(date.getTime())
      ? ''
      : ` · ${date.getFullYear()}. ${pad(date.getMonth() + 1)}.${pad(date.getDate())}.`;
    return `Létrehozta: ${creator}${dateStr}`;
  }

  private merchantName(order: OrderListItemDto): string {
    if (order?.merchant_id === 1 && order?.etsy_shop_id === 2) {
      return 'Jump';
    }
    if (order?.source_key === 'manual' || order?.is_manual === 1) {
      return 'BGFox';
    }
    if (order?.merchant?.name) {
      return order.merchant.name;
    }
    switch (order?.merchant_id) {
      case 1: return 'Etsy';
      case 2: return 'Shopify';
      case 3: return 'WooCommerce';
      case 4: return 'Philibert';
      default: return 'Shop';
    }
  }
}
